import { useEffect, useRef, useState } from "react";
import { RefreshCw, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SNOOZE_MS = 30 * 60 * 1000;

async function fetchIndexSignature() {
  const res = await fetch(`/index.html?nocache=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) return null;
  const text = await res.text();
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return String(hash);
}

export const UpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [note, setNote] = useState(null);
  const baseline = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try { baseline.current = await fetchIndexSignature(); } catch { /* offline: riprova al prossimo check */ }
    };
    const check = async () => {
      try {
        const sig = await fetchIndexSignature();
        if (cancelled || !sig) return;
        if (!baseline.current) { baseline.current = sig; return; }
        if (sig !== baseline.current) setUpdateAvailable(true);
      } catch { /* rete assente: ignora */ }
    };
    init();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (!updateAvailable || !localStorage.getItem("velta_token")) return;
    api.get("/release-notes/latest").then((r) => setNote(r.data || null)).catch(() => {});
  }, [updateAvailable]);

  const doUpdate = () => {
    if (note?.id) localStorage.setItem("velta_seen_release_id", note.id);
    window.location.reload();
  };
  const snooze = () => {
    setUpdateAvailable(false);
    setTimeout(() => setUpdateAvailable(true), SNOOZE_MS);
  };

  if (!updateAvailable) return null;

  if (note?.id) {
    return (
      <div
        data-testid="update-banner"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[min(480px,calc(100vw-2rem))] rounded-lg bg-zinc-900 text-white shadow-2xl border border-zinc-700 p-4"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <RefreshCw className="w-4 h-4 text-emerald-400 animate-spin" style={{ animationDuration: "3s" }} />
            Nuova versione disponibile
          </div>
          <button data-testid="update-later-btn" onClick={snooze} aria-label="Più tardi" className="text-zinc-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5 text-emerald-400 text-sm font-semibold" data-testid="update-note-title">
          <Sparkles className="w-3.5 h-3.5" /> {note.title}
        </div>
        <ul className="mt-1.5 space-y-1 text-sm text-zinc-300" data-testid="update-note-items">
          {(Array.isArray(note.items) ? note.items : []).map((it, i) => (
            <li key={i} className="flex gap-2"><span className="text-emerald-400">•</span><span>{it}</span></li>
          ))}
        </ul>
        <button
          data-testid="update-now-btn"
          onClick={doUpdate}
          className="mt-3 w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-sm font-semibold transition-colors"
        >
          Aggiorna ora
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="update-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 rounded-full bg-zinc-900 text-white shadow-2xl px-5 py-3 border border-zinc-700"
    >
      <RefreshCw className="w-4 h-4 text-emerald-400 animate-spin" style={{ animationDuration: "3s" }} />
      <span className="text-sm font-medium">Nuova versione disponibile</span>
      <button
        data-testid="update-now-btn"
        onClick={doUpdate}
        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-sm font-semibold px-4 py-1.5 transition-colors"
      >
        Aggiorna ora
      </button>
      <button data-testid="update-later-btn" onClick={snooze} aria-label="Più tardi" className="text-zinc-400 hover:text-white transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default UpdateBanner;
